/* eslint-disable @next/next/no-img-element -- ui-core is framework-agnostic and consumers provide optimized asset URLs. */
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TableHTMLAttributes,
} from 'react';

function classes(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

function documentLocale() {
  return typeof document === 'undefined'
    ? 'en-US'
    : document.documentElement.lang || 'en-US';
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'medium',
    loading = false,
    disabled,
    children,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      className={classes('ui-button', `ui-button-${variant}`, `ui-button-${size}`, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <span className="ui-spinner" aria-hidden="true" /> : null}
      <span>{children}</span>
    </button>
  );
});

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & FieldProps
>(function Input({ label, hint, error, id: providedId, className, ...props }, ref) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const descriptionId = `${id}-description`;
  return (
    <div className="ui-field">
      <label className="ui-field-label" htmlFor={id}>
        {label}
      </label>
      <input
        ref={ref}
        id={id}
        className={classes('ui-input', error && 'ui-control-invalid', className)}
        aria-invalid={Boolean(error)}
        aria-describedby={hint || error ? descriptionId : undefined}
        {...props}
      />
      {hint || error ? (
        <span id={descriptionId} className={error ? 'ui-field-error' : 'ui-field-hint'}>
          {error ?? hint}
        </span>
      ) : null}
    </div>
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & FieldProps & { options: readonly SelectOption[] }
>(function Select({ label, hint, error, options, id: providedId, className, ...props }, ref) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const descriptionId = `${id}-description`;
  return (
    <div className="ui-field">
      <label className="ui-field-label" htmlFor={id}>
        {label}
      </label>
      <select
        ref={ref}
        id={id}
        className={classes('ui-select', error && 'ui-control-invalid', className)}
        aria-invalid={Boolean(error)}
        aria-describedby={hint || error ? descriptionId : undefined}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {hint || error ? (
        <span id={descriptionId} className={error ? 'ui-field-error' : 'ui-field-hint'}>
          {error ?? hint}
        </span>
      ) : null}
    </div>
  );
});

export function Checkbox({
  label,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="ui-choice">
      <input type="checkbox" {...props} />
      <span>{label}</span>
    </label>
  );
}

export function Radio({
  label,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="ui-choice">
      <input type="radio" {...props} />
      <span>{label}</span>
    </label>
  );
}

export interface SwitchProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked: boolean;
  label: string;
  onCheckedChange: (checked: boolean) => void;
}

export function Switch({ checked, label, onCheckedChange, className, ...props }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={classes('ui-switch-row', className)}
      onClick={() => onCheckedChange(!checked)}
      {...props}
    >
      <span className="ui-switch-track" aria-hidden="true">
        <span className="ui-switch-thumb" />
      </span>
      <span>{label}</span>
    </button>
  );
}

export type CookieConsentChoice = {
  version: string;
  necessary: true;
  analytics: boolean;
  recordedAt: string;
};

export type CookieConsentCopy = {
  preferencesAria: string;
  title: string;
  descriptionBeforePrivacyLink: string;
  privacyNotice: string;
  descriptionAfterPrivacyLink: string;
  optionalAnalyticsLabel: string;
  optionalAnalyticsDescription: string;
  saveChoices: string;
  acceptOptionalAnalytics: string;
  useNecessaryOnly: string;
  manageChoices: string;
  back: string;
};

const defaultCookieConsentCopy: CookieConsentCopy = {
  preferencesAria: 'cookie preferences',
  title: 'Privacy choices',
  descriptionBeforePrivacyLink: '{{appName}} uses necessary storage to keep the service secure and working. Optional analytics helps us improve the product and is off until you choose it. Read our ',
  privacyNotice: 'Privacy Notice',
  descriptionAfterPrivacyLink: '.',
  optionalAnalyticsLabel: 'Optional analytics',
  optionalAnalyticsDescription: 'Measure aggregate product usage. It is not required to use {{appName}}.',
  saveChoices: 'Save choices',
  acceptOptionalAnalytics: 'Accept optional analytics',
  useNecessaryOnly: 'Use necessary only',
  manageChoices: 'Manage choices',
  back: 'Back',
};

export interface CookieConsentBannerProps {
  appId: string;
  appName: string;
  privacyHref: string;
  copy?: Partial<CookieConsentCopy>;
  version?: string;
  onConsentChange?: (choice: CookieConsentChoice) => void;
}

function cookieConsentStorageKey(appId: string) {
  return `ai-factory:cookie-consent:${appId}`;
}

function readCookieConsent(appId: string, version: string): CookieConsentChoice | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(cookieConsentStorageKey(appId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsentChoice>;
    if (parsed.necessary !== true || typeof parsed.analytics !== 'boolean' ||
      parsed.version !== version || typeof parsed.recordedAt !== 'string') return null;
    return {
      necessary: true,
      analytics: parsed.analytics,
      version: parsed.version,
      recordedAt: parsed.recordedAt,
    };
  } catch {
    return null;
  }
}

export function CookieConsentBanner({
  appId,
  appName,
  privacyHref,
  copy: copyOverrides,
  version = '2026-08-02',
  onConsentChange,
}: CookieConsentBannerProps) {
  const copy = { ...defaultCookieConsentCopy, ...copyOverrides };
  const withAppName = (value: string) => value.replaceAll('{{appName}}', appName);
  const [hydrated, setHydrated] = useState(false);
  const [savedChoice, setSavedChoice] = useState<CookieConsentChoice | null>(null);
  const [showChoices, setShowChoices] = useState(false);
  const [editingSavedChoice, setEditingSavedChoice] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    setSavedChoice(readCookieConsent(appId, version));
    setHydrated(true);
  }, [appId, version]);

  function persist(nextAnalytics: boolean) {
    const choice: CookieConsentChoice = {
      necessary: true,
      analytics: nextAnalytics,
      version,
      recordedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(cookieConsentStorageKey(appId), JSON.stringify(choice));
    setSavedChoice(choice);
    setEditingSavedChoice(false);
    setShowChoices(false);
    onConsentChange?.(choice);
  }

  if (!hydrated) return null;

  if (savedChoice && !editingSavedChoice) return (
    <Button className="ui-cookie-settings-trigger" variant="secondary" size="small" onClick={() => {
      setAnalytics(savedChoice.analytics);
      setShowChoices(true);
      setEditingSavedChoice(true);
    }}>
      {copy.manageChoices}
    </Button>
  );

  return (
    <section className="ui-cookie-consent" aria-label={`${appName} ${copy.preferencesAria}`}>
      <div>
        <strong>{copy.title}</strong>
        <p>
          {withAppName(copy.descriptionBeforePrivacyLink)}
          <a href={privacyHref}>{copy.privacyNotice}</a>
          {copy.descriptionAfterPrivacyLink}
        </p>
      </div>
      {showChoices ? (
        <label className="ui-cookie-choice">
          <input
            type="checkbox"
            checked={analytics}
            onChange={(event) => setAnalytics(event.currentTarget.checked)}
          />
          <span>
            <strong>{copy.optionalAnalyticsLabel}</strong>
            <small>{withAppName(copy.optionalAnalyticsDescription)}</small>
          </span>
        </label>
      ) : null}
      <div className="ui-cookie-actions">
        {showChoices ? (
          <Button variant="primary" size="small" onClick={() => persist(analytics)}>
            {copy.saveChoices}
          </Button>
        ) : (
          <>
            <Button variant="primary" size="small" onClick={() => persist(true)}>
              {copy.acceptOptionalAnalytics}
            </Button>
            <Button variant="secondary" size="small" onClick={() => persist(false)}>
              {copy.useNecessaryOnly}
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="small"
          aria-expanded={showChoices}
          onClick={() => {
            if (editingSavedChoice) {
              setEditingSavedChoice(false);
              setShowChoices(false);
            } else setShowChoices((current) => !current);
          }}
        >
          {showChoices ? copy.back : copy.manageChoices}
        </Button>
      </div>
    </section>
  );
}

export interface RuntimePolicyNoticeProps {
  applicationCode: string;
  appName: string;
  functionsUrl: string;
  /**
   * Product-owned labels for feature flags. The shared component deliberately
   * does not encode a product's feature names or policy keys.
   */
  disabledFeatureLabels?: Readonly<Record<string, string>>;
}

type RuntimePolicy = Record<string, boolean | undefined>;

const defaultRuntimePolicyLabels: Readonly<Record<string, string>> = {
  new_signup_enabled: 'new account creation',
  checkout_enabled: 'checkout',
  generation_enabled: 'new generation requests',
  assistant_enabled: 'assistant requests',
  visual_sales_assistant_enabled: 'AI Sales Assistant',
  assistant_widget_enabled: 'sales widget',
  assistant_rag_enabled: 'assistant knowledge replies',
  assistant_leads_enabled: 'assistant lead capture',
  assistant_handover_enabled: 'assistant human handover',
  assistant_visual_generation_enabled: 'assistant visual generation',
  assistant_analytics_enabled: 'assistant analytics updates',
};

function runtimePolicyLabel(key: string, overrides: Readonly<Record<string, string>> | undefined) {
  return overrides?.[key] ?? defaultRuntimePolicyLabels[key] ?? key.replaceAll('_', ' ');
}

/** A public read-only notice. The protected business APIs independently enforce the same controls. */
export function RuntimePolicyNotice({
  applicationCode,
  appName,
  functionsUrl,
  disabledFeatureLabels,
}: RuntimePolicyNoticeProps) {
  const [policy, setPolicy] = useState<RuntimePolicy | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${functionsUrl.replace(/\/$/, '')}/runtime-policy-api`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ application_code: applicationCode }),
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) return null;
      const payload = await response.json() as { data?: RuntimePolicy };
      return payload.data ?? null;
    }).then((value) => {
      if (!controller.signal.aborted) setPolicy(value);
    }).catch(() => undefined);
    return () => controller.abort();
  }, [applicationCode, functionsUrl]);

  if (!policy) return null;
  const disabled = [
    ...(policy.application_enabled === false ? ['new activity'] : []),
    ...Object.entries(policy)
      .filter(([key, enabled]) => key !== 'application_enabled' && enabled === false)
      .map(([key]) => runtimePolicyLabel(key, disabledFeatureLabels)),
  ];
  if (!disabled.length) return null;
  return (
    <aside className="ui-runtime-policy-notice" role="status" aria-live="polite">
      <strong>{appName} is temporarily limited</strong>
      <span>{disabled.join(', ')} {disabled.length === 1 ? 'is' : 'are'} temporarily unavailable. Existing completed work remains available according to your account access.</span>
    </aside>
  );
}

type GovernancePayload<T> = { data?: T; error?: string };
type ConsentSummary = {
  consent_key: string;
  decision: string;
  document_version: string;
  recorded_at: string;
};
type ExportJobSummary = { id: string; status: string; created_at: string };
type DeletionRequestSummary = { id: string; status: string; created_at: string };

export interface AccountPrivacyControlsProps {
  applicationCode: string;
  appAccountId: string;
  accessToken: string;
  functionsUrl: string;
  appName: string;
}

/**
 * Application-scoped privacy operations.  The API verifies membership and owner
 * permissions again on the server; this UI never chooses a cross-app scope.
 */
export function AccountPrivacyControls({
  applicationCode,
  appAccountId,
  accessToken,
  functionsUrl,
  appName,
}: AccountPrivacyControlsProps) {
  const [consents, setConsents] = useState<ConsentSummary[]>([]);
  const [exports, setExports] = useState<ExportJobSummary[]>([]);
  const [deletions, setDeletions] = useState<DeletionRequestSummary[]>([]);
  const [busy, setBusy] = useState<'export' | 'deletion' | 'verify' | null>(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const invoke = useCallback(async <T,>(body: Record<string, unknown>) => {
    const response = await fetch(`${functionsUrl.replace(/\/$/, '')}/audit-governance-api`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, application_code: applicationCode, app_account_id: appAccountId }),
    });
    const payload = await response.json() as GovernancePayload<T>;
    if (!response.ok || payload.data === undefined) throw new Error(payload.error ?? 'privacy_operation_failed');
    return payload.data;
  }, [accessToken, appAccountId, applicationCode, functionsUrl]);

  const refresh = useCallback(async () => {
    try {
      const [consentResult, exportResult, deletionResult] = await Promise.all([
        invoke<{ consents: ConsentSummary[] }>({ action: 'list_consents' }),
        invoke<{ export_jobs: ExportJobSummary[] }>({ action: 'list_exports' }),
        invoke<{ deletion_requests: DeletionRequestSummary[] }>({ action: 'list_deletions' }),
      ]);
      setConsents(consentResult.consents ?? []);
      setExports(exportResult.export_jobs ?? []);
      setDeletions(deletionResult.deletion_requests ?? []);
    } catch (value) {
      setError(value instanceof Error ? value.message : 'privacy_operation_failed');
    }
  }, [invoke]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function requestExport() {
    setBusy('export'); setError(''); setNotice('');
    try {
      await invoke({ action: 'request_export', idempotency_key: crypto.randomUUID() });
      setNotice('Your application-scoped export has been queued. A completed export is available only for the approved retention period.');
      await refresh();
    } catch (value) {
      setError(value instanceof Error ? value.message : 'privacy_export_failed');
    } finally { setBusy(null); }
  }

  async function requestDeletion() {
    if (!confirmed) return;
    setBusy('deletion'); setError(''); setNotice('');
    try {
      await invoke({
        action: 'request_deletion',
        scope: 'app_account',
        reason: 'Owner requested application-only account deletion from privacy controls.',
      });
      setConfirmed(false);
      setNotice('Deletion was requested. Verify the new request below before the scheduled worker can process it.');
      await refresh();
    } catch (value) {
      setError(value instanceof Error ? value.message : 'privacy_deletion_failed');
    } finally { setBusy(null); }
  }

  async function verifyDeletion(requestId: string) {
    setBusy('verify'); setError(''); setNotice('');
    try {
      await invoke({ action: 'verify_deletion', request_id: requestId });
      setNotice('Deletion request verified. It will be processed according to the application retention and legal-hold rules.');
      await refresh();
    } catch (value) {
      setError(value instanceof Error ? value.message : 'privacy_verification_failed');
    } finally { setBusy(null); }
  }

  return (
    <section className="ui-account-privacy" aria-labelledby="account-privacy-title">
      <header>
        <span>Privacy controls</span>
        <h2 id="account-privacy-title">Your {appName} data</h2>
        <p>These requests are confined to this application account. They never delete your identity or your accounts in other applications.</p>
      </header>
      <div className="ui-account-privacy-grid">
        <article>
          <h3>Export your data</h3>
          <p>Create a portable export of the profile, account, content, and analytics data held for this {appName} account.</p>
          <button type="button" onClick={() => void requestExport()} disabled={busy !== null}>
            {busy === 'export' ? 'Queuing export…' : 'Request data export'}
          </button>
          {exports[0] ? <small>Latest request: {exports[0].status.replaceAll('_', ' ')} · {new Date(exports[0].created_at).toLocaleString(documentLocale())}</small> : null}
        </article>
        <article className="ui-account-privacy-danger">
          <h3>Delete this application account</h3>
          <p>Deletion affects only this {appName} account. Required billing evidence may be retained and an active legal hold prevents deletion.</p>
          <label>
            <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.currentTarget.checked)} />
            I understand this does not delete my other application accounts.
          </label>
          <button type="button" onClick={() => void requestDeletion()} disabled={!confirmed || busy !== null}>
            {busy === 'deletion' ? 'Requesting deletion…' : 'Request application deletion'}
          </button>
          {deletions.filter((item) => item.status === 'requested').map((item) => (
            <button className="ui-account-privacy-verify" type="button" key={item.id} onClick={() => void verifyDeletion(item.id)} disabled={busy !== null}>
              {busy === 'verify' ? 'Verifying…' : 'Verify pending deletion'}
            </button>
          ))}
        </article>
      </div>
      <section className="ui-account-privacy-audit" aria-label="Consent audit">
        <h3>Consent record</h3>
        {consents.length ? <ul>{consents.map((item) => <li key={`${item.consent_key}-${item.recorded_at}`}><strong>{item.consent_key.replaceAll('_', ' ')}</strong><span>{item.decision} · v{item.document_version} · {new Date(item.recorded_at).toLocaleDateString(documentLocale())}</span></li>)}</ul> : <p>No consent records have been captured for this account yet.</p>}
      </section>
      {notice ? <p className="ui-account-privacy-notice" role="status">{notice}</p> : null}
      {error ? <p className="ui-account-privacy-error" role="alert">{error.replaceAll('_', ' ')}</p> : null}
    </section>
  );
}

export interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
  disabled?: boolean;
}

export function Tabs({
  items,
  activeId,
  onChange,
  label = 'Sections',
}: {
  items: readonly TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  label?: string;
}) {
  const idPrefix = useId();
  const enabled = items.filter((item) => !item.disabled);
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, currentId: string) {
    const index = enabled.findIndex((item) => item.id === currentId);
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? enabled.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length;
    const next = enabled[nextIndex];
    if (next) {
      onChange(next.id);
      document.getElementById(`${idPrefix}-tab-${next.id}`)?.focus();
    }
  }
  return (
    <div className="ui-tabs">
      <div role="tablist" aria-label={label} className="ui-tab-list">
        {items.map((item) => (
          <button
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${item.id}`}
            key={item.id}
            aria-selected={activeId === item.id}
            aria-controls={`${idPrefix}-panel-${item.id}`}
            tabIndex={activeId === item.id ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            onKeyDown={(event) => onKeyDown(event, item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          role="tabpanel"
          id={`${idPrefix}-panel-${item.id}`}
          aria-labelledby={`${idPrefix}-tab-${item.id}`}
          hidden={activeId !== item.id}
          className="ui-tab-panel"
        >
          {item.content}
        </div>
      ))}
    </div>
  );
}

interface OverlayProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

function useOverlay(open: boolean, onClose: () => void) {
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    returnFocusRef.current = document.activeElement as HTMLElement;
    panelRef.current?.focus();
    const listener = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
      document.body.style.overflow = previousOverflow;
      returnFocusRef.current?.focus();
    };
  }, [onClose, open]);
  return panelRef;
}

export function Dialog({ open, title, description, onClose, children, footer }: OverlayProps) {
  const titleId = useId();
  const descriptionId = useId();
  const ref = useOverlay(open, onClose);
  if (!open) return null;
  return (
    <div
      className="ui-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className="ui-dialog"
      >
        <div className="ui-overlay-header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <Button variant="ghost" size="small" aria-label="Close dialog" onClick={onClose}>
            ×
          </Button>
        </div>
        <div className="ui-overlay-body">{children}</div>
        {footer ? <div className="ui-overlay-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export function Drawer({ open, title, description, onClose, children, footer }: OverlayProps) {
  const titleId = useId();
  const descriptionId = useId();
  const ref = useOverlay(open, onClose);
  if (!open) return null;
  return (
    <div
      className="ui-overlay ui-drawer-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className="ui-drawer"
      >
        <div className="ui-overlay-header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <Button variant="ghost" size="small" aria-label="Close drawer" onClick={onClose}>
            ×
          </Button>
        </div>
        <div className="ui-overlay-body">{children}</div>
        {footer ? <div className="ui-overlay-footer">{footer}</div> : null}
      </aside>
    </div>
  );
}

export function Toast({
  title,
  message,
  tone = 'info',
  onDismiss,
}: {
  title: string;
  message?: string;
  tone?: 'info' | 'success' | 'warning' | 'error';
  onDismiss?: () => void;
}) {
  return (
    <div
      className={classes('ui-toast', `ui-toast-${tone}`)}
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
    >
      <div>
        <strong>{title}</strong>
        {message ? <p>{message}</p> : null}
      </div>
      {onDismiss ? (
        <Button variant="ghost" size="small" aria-label="Dismiss notification" onClick={onDismiss}>
          ×
        </Button>
      ) : null}
    </div>
  );
}

export interface TableColumn<Row> {
  key: keyof Row | string;
  header: string;
  render?: (row: Row) => ReactNode;
}

export function Table<Row extends { id: string }>({
  columns,
  rows,
  caption,
  ...props
}: {
  columns: readonly TableColumn<Row>[];
  rows: readonly Row[];
  caption: string;
} & TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div className="ui-table-scroll" tabIndex={0} aria-label={`${caption} scroll area`}>
      <table className="ui-table" {...props}>
        <caption>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={String(column.key)} scope="col">
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {columns.map((column) => (
                <td key={String(column.key)}>
                  {column.render ? column.render(row) : String(row[column.key as keyof Row] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  label = 'Pagination',
}: {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  label?: string;
}) {
  const pages = Array.from({ length: pageCount }, (_, index) => index + 1);
  return (
    <nav aria-label={label} className="ui-pagination">
      <Button
        variant="secondary"
        size="small"
        disabled={page <= 1}
        aria-label="Previous page"
        onClick={() => onPageChange(page - 1)}
      >
        ‹
      </Button>
      {pages.map((number) => (
        <button
          type="button"
          key={number}
          aria-current={page === number ? 'page' : undefined}
          onClick={() => onPageChange(number)}
        >
          {number}
        </button>
      ))}
      <Button
        variant="secondary"
        size="small"
        disabled={page >= pageCount}
        aria-label="Next page"
        onClick={() => onPageChange(page + 1)}
      >
        ›
      </Button>
    </nav>
  );
}

interface StateProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
}
function StatePanel({ title, description, icon, action, className, ...props }: StateProps) {
  return (
    <div className={classes('ui-state', className)} {...props}>
      {icon ? (
        <div className="ui-state-icon" aria-hidden="true">
          {icon}
        </div>
      ) : null}
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
      {action}
    </div>
  );
}
export function EmptyState(props: StateProps) {
  return <StatePanel {...props} className={classes('ui-empty-state', props.className)} />;
}
export function ErrorState(props: StateProps) {
  return (
    <StatePanel role="alert" {...props} className={classes('ui-error-state', props.className)} />
  );
}

/**
 * A route-level fallback for unexpected render and data-loader failures.
 * Callers provide localized copy; this component deliberately renders no
 * exception details so browser users never receive a stack trace.
 */
export function ApplicationFailureState({
  title,
  description,
  retryLabel,
  onRetry,
  supportHref,
  supportLabel,
}: {
  title: string;
  description: string;
  retryLabel: string;
  onRetry: () => void;
  /** A configured, application-owned support destination. */
  supportHref?: string;
  supportLabel?: string;
}) {
  return (
    <main className="ui-route-failure" aria-live="assertive">
      <ErrorState
        title={title}
        description={description}
        icon="!"
        action={
          <div className="ui-state-actions">
            <Button type="button" onClick={onRetry}>{retryLabel}</Button>
            {supportHref && supportLabel ? (
              <a className="ui-button ui-button-secondary ui-button-medium" href={supportHref}>
                <span>{supportLabel}</span>
              </a>
            ) : null}
          </div>
        }
      />
    </main>
  );
}

export function Skeleton({
  lines = 3,
  label = 'Loading content',
}: {
  lines?: number;
  label?: string;
}) {
  return (
    <div className="ui-skeleton" role="status" aria-label={label}>
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} style={{ width: `${100 - index * 13}%` }} />
      ))}
    </div>
  );
}

export function CreditBadge({
  value,
  label = 'Credits',
}: {
  value: number | string;
  label?: string;
}) {
  return (
    <span className="ui-credit-badge" aria-label={`${value} ${label}`}>
      <span aria-hidden="true">✦</span>
      <strong>{value}</strong>
      <span>{label}</span>
    </span>
  );
}

export function UpgradeGate({
  title,
  description,
  actionLabel = 'Upgrade',
  onUpgrade,
  children,
}: {
  title: string;
  description: string;
  actionLabel?: string;
  onUpgrade: () => void;
  children?: ReactNode;
}) {
  const titleId = useId();
  return (
    <section className="ui-upgrade-gate" aria-labelledby={titleId}>
      {children ? (
        <div className="ui-upgrade-preview" aria-hidden="true">
          {children}
        </div>
      ) : null}
      <div>
        <span className="ui-state-icon" aria-hidden="true">
          ◇
        </span>
        <h3 id={titleId}>{title}</h3>
        <p>{description}</p>
        <Button onClick={onUpgrade}>{actionLabel}</Button>
      </div>
    </section>
  );
}

export interface FileUploaderProps {
  label: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
}

export function FileUploader({ label, accept, multiple, disabled, onFiles }: FileUploaderProps) {
  const id = useId();
  const [dragging, setDragging] = useState(false);
  function choose(event: ChangeEvent<HTMLInputElement>) {
    onFiles(Array.from(event.target.files ?? []));
  }
  return (
    <label
      htmlFor={id}
      className={classes(
        'ui-uploader',
        dragging && 'ui-uploader-dragging',
        disabled && 'ui-uploader-disabled',
      )}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        if (!disabled) onFiles(Array.from(event.dataTransfer.files));
      }}
    >
      <span className="ui-uploader-icon" aria-hidden="true">
        ⇧
      </span>
      <strong>{label}</strong>
      <span>Drag and drop or browse</span>
      <input
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={choose}
      />
    </label>
  );
}

export function ImageCompare({
  beforeSrc,
  afterSrc,
  beforeAlt,
  afterAlt,
  value,
  onChange,
}: {
  beforeSrc: string;
  afterSrc: string;
  beforeAlt: string;
  afterAlt: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <figure className="ui-image-compare">
      <img src={beforeSrc} alt={beforeAlt} />
      <div className="ui-image-after" style={{ clipPath: `inset(0 0 0 ${value}%)` }}>
        <img src={afterSrc} alt={afterAlt} />
      </div>
      <input
        type="range"
        min="0"
        max="100"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        aria-label="Image comparison position"
      />
      <figcaption>
        <span>Before</span>
        <span>After</span>
      </figcaption>
    </figure>
  );
}

export function ChatBubble({
  author,
  children,
  timestamp,
  direction = 'incoming',
  status,
}: {
  author: string;
  children: ReactNode;
  timestamp?: string;
  direction?: 'incoming' | 'outgoing';
  status?: string;
}) {
  return (
    <article
      className={classes('ui-chat-bubble', `ui-chat-${direction}`)}
      aria-label={`${direction} message from ${author}`}
    >
      <strong>{author}</strong>
      <div>{children}</div>
      {timestamp || status ? (
        <footer>
          {timestamp}
          {timestamp && status ? ' · ' : ''}
          {status}
        </footer>
      ) : null}
    </article>
  );
}

export function ProductCard({
  title,
  imageSrc,
  imageAlt,
  subtitle,
  price,
  badge,
  action,
}: {
  title: string;
  imageSrc: string;
  imageAlt: string;
  subtitle?: string;
  price?: string;
  badge?: string;
  action?: ReactNode;
}) {
  return (
    <article className="ui-product-card">
      <div className="ui-card-image">
        <img src={imageSrc} alt={imageAlt} />
        {badge ? <span>{badge}</span> : null}
      </div>
      <div className="ui-card-body">
        <h3>{title}</h3>
        {subtitle ? <p>{subtitle}</p> : null}
        {price ? <strong>{price}</strong> : null}
        {action}
      </div>
    </article>
  );
}

export function TarotCard({
  name,
  imageSrc,
  imageAlt,
  meaning,
  selected = false,
  onSelect,
  disabled = false,
}: {
  name: string;
  imageSrc: string;
  imageAlt: string;
  meaning?: string;
  selected?: boolean;
  onSelect?: () => void;
  disabled?: boolean;
}) {
  const content = (
    <>
      <img src={imageSrc} alt={imageAlt} />
      <span className="ui-tarot-card-copy">
        <strong>{name}</strong>
        {meaning ? <small>{meaning}</small> : null}
      </span>
    </>
  );
  return onSelect ? (
    <button
      type="button"
      className="ui-tarot-card"
      aria-pressed={selected}
      onClick={onSelect}
      disabled={disabled}
    >
      {content}
    </button>
  ) : (
    <figure className="ui-tarot-card">{content}</figure>
  );
}

export function RestrictedState(props: StateProps) {
  return <StatePanel {...props} className={classes('ui-restricted-state', props.className)} />;
}
